/**
 * Best-effort song-duration probe used by the audio inspector scrubber.
 *
 * - File tracks: build a hidden Audio element with preload="metadata" and
 *   read .duration. Most formats expose duration in <100ms once metadata
 *   is parsed. data: URLs work the same way.
 * - YouTube tracks: spin up an off-screen YT.Player, wait for onReady,
 *   call getDuration(). The player is destroyed after the read so we don't
 *   leak iframe state.
 * - SoundCloud / Spotify: no public duration without an API key, so we
 *   resolve null and the scrubber falls back to a 5-minute lane.
 *
 * Results are cached by a stable key (src or videoId) so opening the
 * inspector for the same track repeatedly is free after the first read.
 */
import type { AudioTrack } from '../types';

const cache = new Map<string, number>();
const inflight = new Map<string, Promise<number | null>>();
const subscribers = new Set<() => void>();

const YT_API_SCRIPT = 'https://www.youtube.com/iframe_api';

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

function trackKey(t: AudioTrack): string {
  if (t.kind === 'youtube') return `yt:${t.videoId}`;
  if (t.kind === 'file') return `file:${t.src.slice(0, 64)}:${t.src.length}`;
  return `${t.kind}:${t.url}`;
}

/** Subscribe to "duration discovered" notifications so a UI can re-render
 *  the scrubber lane width once the real number arrives. */
export function subscribeDuration(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notify(): void {
  for (const fn of subscribers) fn();
}

/** Synchronous getter — returns the cached duration or null. Pair with
 *  ensureAudioDuration() to kick off discovery. */
export function getCachedDuration(track: AudioTrack): number | null {
  return cache.get(trackKey(track)) ?? null;
}

/** Returns ms duration once known, or null if the source can't be probed. */
export async function ensureAudioDuration(track: AudioTrack): Promise<number | null> {
  const key = trackKey(track);
  const cached = cache.get(key);
  if (cached != null) return cached;
  const pending = inflight.get(key);
  if (pending) return pending;

  let probe: Promise<number | null>;
  if (track.kind === 'file') probe = probeFile(track.src);
  else if (track.kind === 'youtube') probe = probeYoutube(track.videoId);
  else probe = Promise.resolve(null);

  inflight.set(key, probe);
  const ms = await probe;
  inflight.delete(key);
  if (ms != null && Number.isFinite(ms) && ms > 0) {
    cache.set(key, ms);
    notify();
    return ms;
  }
  return null;
}

function probeFile(src: string): Promise<number | null> {
  return new Promise((resolve) => {
    const a = document.createElement('audio');
    a.preload = 'metadata';
    let done = false;
    const finish = (v: number | null): void => {
      if (done) return;
      done = true;
      a.removeAttribute('src');
      a.load();
      resolve(v);
    };
    a.addEventListener('loadedmetadata', () => {
      const d = a.duration;
      if (Number.isFinite(d) && d > 0) finish(Math.round(d * 1000));
      else finish(null);
    });
    a.addEventListener('error', () => finish(null));
    // Watchdog — a stuck request shouldn't pin the inspector.
    setTimeout(() => finish(null), 8000);
    try {
      a.src = src;
    } catch {
      finish(null);
    }
  });
}

let ytApiReady: Promise<void> | null = null;
function ensureYtApi(): Promise<void> {
  if (ytApiReady) return ytApiReady;
  ytApiReady = new Promise<void>((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    if (!document.querySelector(`script[src="${YT_API_SCRIPT}"]`)) {
      const s = document.createElement('script');
      s.src = YT_API_SCRIPT;
      s.async = true;
      document.head.appendChild(s);
    }
  });
  return ytApiReady;
}

function probeYoutube(videoId: string): Promise<number | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: number | null): void => {
      if (done) return;
      done = true;
      try {
        player?.destroy?.();
      } catch {
        /* noop */
      }
      host.remove();
      resolve(v);
    };
    const host = document.createElement('div');
    Object.assign(host.style, {
      position: 'fixed',
      left: '-9999px',
      top: '-9999px',
      width: '320px',
      height: '180px',
      pointerEvents: 'none',
      visibility: 'hidden',
    } satisfies Partial<CSSStyleDeclaration>);
    const inner = document.createElement('div');
    host.appendChild(inner);
    document.body.appendChild(host);

    let player: any = null;
    ensureYtApi().then(() => {
      try {
        player = new window.YT.Player(inner, {
          videoId,
          playerVars: { controls: 0, autoplay: 0, mute: 1 },
          events: {
            onReady: () => {
              const dur = Number(player.getDuration?.() ?? 0);
              if (Number.isFinite(dur) && dur > 0) finish(Math.round(dur * 1000));
              else finish(null);
            },
            onError: () => finish(null),
          },
        });
      } catch {
        finish(null);
      }
    });
    setTimeout(() => finish(null), 12000);
  });
}
